import React, { useState, useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { User as UserIcon } from '@phosphor-icons/react'

interface UserAvatarProps {
  user: {
    full_name: string | null
    avatar_url?: string | null
    google_avatar_url?: string | null
    apple_avatar_url?: string | null
  } | null
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'custom'
  grayscale?: boolean
}

/**
 * 👤 UserAvatar - Proje genelinde kullanılan akıllı avatar bileşeni.
 *
 * Öncelik sırası:
 * 1. Google Avatar URL
 * 2. Apple Avatar URL
 * 3. Manuel/Özel Avatar URL
 * 4. İsim Başharfleri (Initials)
 *
 * Herhangi bir URL yüklenemezse (404, CORS vb.) otomatik olarak bir sonraki seçeneğe düşer.
 */
export function UserAvatar({ user, className, size = 'md', grayscale = false }: UserAvatarProps) {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null)
  const [errorCount, setErrorCount] = useState(0)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  // 🎯 Hiyerarşik Kaynak Listesi: Önce aktif manuel resim, sonra sosyal medya resimleri.
  const sources = useMemo(() => {
    if (user?.avatar_url === 'initials') return []
    return [user?.avatar_url, user?.google_avatar_url, user?.apple_avatar_url].filter(
      src => src && typeof src === 'string' && src.startsWith('http')
    ) as string[]
  }, [user?.avatar_url, user?.google_avatar_url, user?.apple_avatar_url])

  // 🛡️ Güvenli Önbellek Kırıcı: Google/Apple resimlerini her gün tazelemek için.
  const applyDailyCacheBuster = (url: string) => {
    if (!url || typeof url !== 'string' || !url.startsWith('http')) return url
    if (!url.includes('googleusercontent') && !url.includes('apple')) return url

    try {
      const parsedUrl = new URL(url)
      const today = new Date().toISOString().split('T')[0] // '2023-10-24'
      parsedUrl.searchParams.set('v', today)
      return parsedUrl.toString()
    } catch {
      return url
    }
  }

  useEffect(() => {
    // Kullanıcı veya resimler değiştiğinde her şeyi sıfırla
    setIsImageLoaded(false)
    setErrorCount(0)

    if (sources.length > 0) {
      setCurrentSrc(applyDailyCacheBuster(sources[0]))
    } else {
      setCurrentSrc(null)
    }
  }, [sources])

  const handleError = () => {
    const nextIndex = errorCount + 1
    if (nextIndex < sources.length) {
      setErrorCount(nextIndex)
      setCurrentSrc(applyDailyCacheBuster(sources[nextIndex]))
    } else {
      setCurrentSrc(null) // Tüm kaynaklar denendi ve başarısız oldu
    }
  }

  const getInitials = (name: string) => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toLocaleUpperCase('tr-TR')
    return parts[0][1] ? (parts[0][0] + parts[0][1]).toLocaleUpperCase('tr-TR') : parts[0][0].toLocaleUpperCase('tr-TR')
  }

  const getAvatarColor = (_name: string) => {
    return 'bg-zinc-800' // Antrasit Gri
  }

  const sizeClasses = {
    sm: 'size-8 text-[10px]',
    md: 'size-10 text-xs',
    lg: 'size-14 text-base',
    xl: 'size-36 text-4xl',
    custom: '', // className üzerinden yönetilir
  }

  const commonClasses = cn(
    'rounded-full shrink-0 transition-all duration-300',
    grayscale && 'grayscale',
    size !== 'custom' && sizeClasses[size],
    className
  )

  const displayName = user?.full_name?.trim() || ''

  // 🎨 Durum 1: Hiç resim yoksa veya yükleme başarısız olduysa
  if (!currentSrc) {
    if (displayName.length < 2) {
      return (
        <div
          className={cn(
            'flex items-center justify-center bg-[#FDF8E8] border border-[#f5ebd1] shadow-inner overflow-hidden',
            commonClasses
          )}
        >
          <UserIcon weight="fill" className="text-[#D4AF37]/50 w-1/2 h-1/2" />
        </div>
      )
    }

    const initials = getInitials(displayName)
    const colorClass = getAvatarColor(displayName)
    return (
      <div
        className={cn(
          'flex items-center justify-center font-bold text-white shadow-inner animate-in fade-in duration-500',
          commonClasses,
          colorClass
        )}
      >
        <span className="leading-none pt-[1px]">{initials}</span>
      </div>
    )
  }

  // 🖼️ Durum 2: Resim kaynağı var
  return (
    <div className={cn('bg-[#FDF8E8] relative overflow-hidden', commonClasses)}>
      {!isImageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center border border-[#f5ebd1] rounded-full">
          <UserIcon weight="fill" className="text-[#D4AF37]/50 w-1/2 h-1/2 animate-pulse" />
        </div>
      )}
      <img
        src={currentSrc}
        alt={displayName || 'Avatar'}
        referrerPolicy="no-referrer"
        className={cn(
          'w-full h-full object-cover transition-opacity duration-500',
          isImageLoaded ? 'opacity-100' : 'opacity-0'
        )}
        onLoad={() => setIsImageLoaded(true)}
        onError={handleError}
      />
    </div>
  )
}
